import { ArrowRightIcon, CalendarAltIcon, LocationAltIcon, PhoneIcon } from 'cokitchen-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as LoopIcon } from 'assets/icons/loop.svg';
import { ReactComponent as NoteIcon } from 'assets/icons/note.svg';
import PaymentOptions, { PaymentMethod } from 'components/payment-options';
import { Button, ToastMessage, formatPhoneNumber } from '@cokitchen/cokitchen-components';
import useDashboardContext from 'contexts/dashboard.context';
import DeliveryNotificationModal from 'components/delivery-notification-modal';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { usePaystackPayment } from 'react-paystack';
import useOrdersContext from 'contexts/orders.context';
import {
  formatCurrency,
  formatDatetime,
  formatDaysOfWeek,
  formatToSentenceCase,
  getTotal,
  handleApiError
} from 'utils/functions';
import { STORAGE_KEYS } from 'utils/constants';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { ENUM_ORDERS_TYPE } from 'utils/enum';
import TransferPaymentModal from 'components/modals/bank-transfer-modal';

const Checkout = () => {
  const {
    setBackLinkTitle,
    setIsBackLink,
    paymentType,
    paymentView,
    setPaymentView,
    setPaymentLoading,
    activePaymentCard,
    activeCompany,
    paystackProps,
    setPaystackProps,
    companyWalletDetails,
    setPaymentAmount
  } = useDashboardContext();
  const {
    calculatedOrderForm,
    bulkOrderMeals,
    batchOrderMeals,
    calculatedOrder,
    createOrderForm,
    setCreateOrderForm,
    orderType,
    handleReset
  } = useOrdersContext();
  const [showDeliveryNotificationModal, setShowDeliveryNotificationModal] = useState(false);
  const initializePayment: any = usePaystackPayment(paystackProps as any);
  const [searchParams] = useSearchParams();
  const [disabled, setDisabled] = useState(false);

  const [loading, setLoading] = useState(false);
  const [openTransferPaymentModal, setOpenTransferPaymentModal] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [orderTypeId, setOrderTypeId] = useState('');
  const BANI_PUBLIC_KEY = process.env.REACT_APP_BANI_PUBLIC_KEY;
  const typeOfOrder = useMemo(() => {
    return (searchParams.get('type') as ENUM_ORDERS_TYPE) ?? '';
  }, [searchParams]);
  const brandIds = calculatedOrder?.brand_ids;
  const DEFAULT_PHONE_NUMBER = localStorage.getItem(STORAGE_KEYS.PHONE_NUMBER);

  const deliveryDatetime = calculatedOrderForm.preorder
    ? formatDatetime(
        calculatedOrderForm.preorder_delivery_date ?? '',
        calculatedOrderForm.preorder_delivery_time ?? ''
      )
    : formatDatetime(
        calculatedOrderForm.scheduled_delivery_date ?? '',
        calculatedOrderForm.scheduled_delivery_time ?? ''
      );

  useEffect(() => {
    const orderInfo = createOrderForm.order_details;
    let orderId = '';
    orderType.map(({ id, name }) => {
      if (name === 'CARD' && orderInfo.use_paystack) {
        orderId = id;
      }
      if (name === 'WALLET' && orderInfo.use_business_wallet) {
        orderId = id;
      }
      if (name === 'BANK' && orderInfo.use_transfer) {
        orderId = id;
      }
    });
    setOrderTypeId(orderId);
  }, [createOrderForm?.order_details]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to Basket');
    setPaymentAmount(calculatedOrder?.total_amount?.toString());
    setPaystackProps({
      ...paystackProps,
      metadata: {
        customer: {
          order: true,
          webhook: true,
          phone_number: activeCompany?.user?.phone_number,
          body: {
            order_details: {
              cutlery: true
            },
            company_id: activeCompany.id,
            calculated_order_id: calculatedOrder?.id,
            order_type_id: orderTypeId
          }
        }
      }
    });
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  const handleOnSuccess = async () => {
    toast(<ToastMessage type='success' message='Payment successful, processing wallet balance' />);
    completeOrder();
  };
  const navigate = useNavigate();

  const handleBaniTopup = () => {
    (window as any).BaniPopUp({
      amount: calculatedOrder?.total_amount,
      phoneNumber: formatPhoneNumber(activeCompany.user.phone_number),
      merchantKey: BANI_PUBLIC_KEY,
      email: activeCompany.user.email,
      firstName: activeCompany.user.first_name,
      lastName: activeCompany.user.last_name,
      bankTransfer: true,
      metadata: {
        customer: {
          order: true,
          webhook: true,
          phone_number: formatPhoneNumber(activeCompany?.user?.phone_number),
          body: {
            order_details: {
              cutlery: true
            },
            company_id: activeCompany.id,
            calculated_order_id: calculatedOrder?.id,
            order_type_id: orderTypeId
          }
        }
      },
      callback: handleOnSuccess
    });
  };

  const handleDeliveryNotificationModal = () => {
    setShowDeliveryNotificationModal(!showDeliveryNotificationModal);
  };
  async function handlePayment() {
    if (paymentType === 'transfer' && paymentView === 'methods') return setPaymentView('pay');
    if (paymentType === 'transfer') {
      handleBaniTopup();
      return;
    }
    if (paymentType === 'paystack' && activePaymentCard && paymentView === 'methods')
      return setPaymentView('pay');
    setPaymentLoading(true);
    if (paymentType === 'paystack' && !activePaymentCard)
      return initializePayment(
        (context: any) => {
          if (context.status === 'success') {
            toast(
              <ToastMessage
                type='success'
                message='Payment successful, processing wallet balance'
              />
            );
            completeOrder();
          } else toast(<ToastMessage type='error' message='Payment failed' />);
          setPaymentLoading(false);
        },
        () => {
          setPaymentLoading(false);
        }
      );
    if (!activeCompany?.id) return;

    setPaymentLoading(false);
  }
  const updatePaymentMethod = (payMethod: PaymentMethod) => {
    setCreateOrderForm({
      ...createOrderForm,
      order_details: {
        ...createOrderForm.order_details,
        use_business_wallet: payMethod.use_business_wallet,
        use_transfer: payMethod.transfer,
        use_paystack: payMethod.use_paystack
      }
    });
  };

  const isDisabled = () => {
    if (
      (createOrderForm.order_details.use_paystack ||
        createOrderForm.order_details.use_business_wallet ||
        createOrderForm.order_details.use_transfer) &&
      calculatedOrder?.id
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };
  useEffect(() => {
    isDisabled();
  }, [createOrderForm.order_details]);

  const completeOrder = async () => {
    setLoading(true);
    try {
      const data = await apiService.post('order', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: {
          order_type_id: orderTypeId,
          calculated_order_id: calculatedOrder?.id,
          order_details: {
            rider_note:
              createOrderForm.order_details.rider_note === ''
                ? undefined
                : createOrderForm.order_details.rider_note,
            cutlery: createOrderForm.order_details.cutlery,
            use_bani: paymentType === 'transfer',
            bank_transfer: false,
            ...(paymentType === 'wallet' && {
              wallets: [
                {
                  wallet_amount: calculatedOrder?.total_amount?.toString(),
                  wallet_id: companyWalletDetails?.id
                }
              ]
            })
          }
        }
      });
      if (data.data.order) setOrderId(data.data.order.id);

      toast(<ToastMessage type='success' message='Payment successful, processing order' />);
      handleReset();
      if (paymentType === 'transfer') {
        setOpenTransferPaymentModal(true);
        return;
      }
      navigate('/orders');
    } catch (err) {
      handleApiError(err, 'Payment failed');
    } finally {
      setPaymentLoading(false);
      setLoading(false);
    }
  };

  return (
    <div className='max-w-[450px] mx-auto'>
      <p className='font-semibold text-2xl'>
        {typeOfOrder === ENUM_ORDERS_TYPE.BULK || typeOfOrder === ENUM_ORDERS_TYPE.BATCH
          ? 'Checkout'
          : 'Group checkout'}
      </p>
      {typeOfOrder === ENUM_ORDERS_TYPE.BULK ? (
        <p className='text-gray-9'>
          {brandIds?.length} {brandIds?.length > 1 ? 'brands' : 'brand'}, {getTotal(bulkOrderMeals)}{' '}
          {getTotal(bulkOrderMeals) > 1 ? 'items' : 'item'}
        </p>
      ) : typeOfOrder === ENUM_ORDERS_TYPE.BATCH ? (
        <p className='text-gray-9'>
          {brandIds?.length} {brandIds?.length > 1 ? 'brands' : 'brand'},{' '}
          {getTotal(batchOrderMeals)} {getTotal(batchOrderMeals) > 1 ? 'items' : 'item'}
        </p>
      ) : (
        <p className='text-gray-9'>
          {' '}
          {calculatedOrder.sub_calculated_order_ids?.length} participants
        </p>
      )}

      <div className=''>
        <div className='border-b border-b-[#EDEDED] py-4 flex gap-3 items-center mt-10'>
          <span>
            <CalendarAltIcon color='#96999C' />
          </span>
          <p className='text-sm'>{deliveryDatetime}</p>
        </div>
        {calculatedOrderForm.recurring && (
          <div className='border-b border-b-[#EDEDED] py-4 flex gap-3 items-center'>
            <span>
              <LoopIcon />{' '}
            </span>{' '}
            <p className='text-gray-10 text-sm capitalize'>
              {formatDaysOfWeek(calculatedOrderForm.selected_days)} until {''}
              {moment(calculatedOrderForm.recurring_order_end_date).format('Do MMM YYYY')}
            </p>
          </div>
        )}
        <div className='border-b border-b-[#EDEDED] py-4 flex gap-3 items-center'>
          <span>
            <LocationAltIcon color='#96999C' />
          </span>
          <p className='text-sm'>
            {calculatedOrderForm.address_details?.address_line
              ? calculatedOrderForm.address_details?.address_line +
                ', ' +
                calculatedOrderForm.address_details?.city
              : activeCompany.address}
          </p>
        </div>
        <div className='py-4 flex gap-3 items-center'>
          <span>
            <PhoneIcon color='#96999C' />
          </span>
          <p className='text-sm'>
            {calculatedOrderForm?.address_details?.phone_number || DEFAULT_PHONE_NUMBER}{' '}
          </p>
        </div>
      </div>
      <div
        onClick={handleDeliveryNotificationModal}
        className='min-w-full flex items-center cursor-pointer justify-between  text-sm border-y-[8px] border-y-[#F7F7F7] py-5 my-1'>
        <div className='flex gap-3 items-center'>
          <NoteIcon />
          {createOrderForm.order_details.rider_note ? (
            <p className='line-clamp-1 text-gray-10'>
              {formatToSentenceCase(createOrderForm.order_details.rider_note)}
            </p>
          ) : (
            <p className='text-[#3FB827]'>Add delivery instructions</p>
          )}
          <p className='line-clamp-1'></p>
        </div>
        <ArrowRightIcon color='#2E3031' />
      </div>
      <p className='text-lg font-medium pt-6 pb-4'>Payment</p>

      <PaymentOptions
        amount={calculatedOrder?.total_amount}
        handleUpdatePaymentMethod={updatePaymentMethod}
      />
      <div className='flex justify-between font-medium items-center mt-14 mb-5'>
        <p>Amount due</p>
        <p>{formatCurrency(calculatedOrder?.total_amount)}</p>
      </div>
      <Button
        color='primary'
        variant='block'
        small
        onClick={() => {
          if (paymentType === 'paystack') {
            handlePayment();
          } else {
            completeOrder();
          }
        }}
        className=' font-medium text-center text-[14px] w-full mx-auto mt-6'
        disabled={disabled}
        loading={loading}>
        Complete order
      </Button>
      <DeliveryNotificationModal
        show={showDeliveryNotificationModal}
        onClose={handleDeliveryNotificationModal}
        setValue={(v) => {
          setCreateOrderForm({
            ...createOrderForm,
            order_details: { ...createOrderForm.order_details, rider_note: v }
          });
        }}
      />

      {openTransferPaymentModal && (
        <TransferPaymentModal
          isOpen={openTransferPaymentModal}
          onRequestClose={() => setOpenTransferPaymentModal(false)}
          orderId={orderId}
        />
      )}
    </div>
  );
};

export default Checkout;
